import type { CSSProperties } from "react";

type Tone = "success" | "warning" | "danger" | "neutral";
type Size = "sm" | "md";

type StatusBadgeProps = {
  label: string;
  tone?: Tone;
  size?: Size;
  style?: CSSProperties;
  className?: string;
};

// Feedback système ≠ marque : le ton warning porte la famille warn* dédiée —
// un avertissement peint en or-50/or-700 (marque) était visuellement identique
// à un accent brand (pin de LocationPill, chips…). Le texte pioche toujours le
// cran 600, calibré ≥ 4.5:1 sur fond clair (success-500 en texte = 2.6:1).
const toneClass: Record<Tone, { wrap: string; text: string }> = {
  success: { wrap: "bg-success-100", text: "text-success-600" },
  warning: { wrap: "bg-warn-50", text: "text-warn-600" },
  danger: { wrap: "bg-sos-200", text: "text-sos-600" },
  neutral: { wrap: "bg-gray-100", text: "text-gray-500" },
};

const sizeClass: Record<Size, { wrap: string; text: string }> = {
  sm: { wrap: "px-2 py-0.5", text: "text-[10px] tracking-wider" },
  md: { wrap: "px-3 py-1.5", text: "text-xs tracking-widest" },
};

/**
 * Pastille d'état.
 *
 * Les classes Tailwind traversent le portage sans changement — c'est l'intérêt
 * d'avoir gardé la même configuration de thème. Seul `font-typold-bold`
 * devient `font-bold` : sur web, une famille porte ses graisses (cf. la table
 * de conversion en tête de tailwind.config.js).
 *
 * ⚠️ Le libellé est un `<span>` nu et NON un `<AppText>`, contrairement à la
 * version native. `AppText` pose sa typographie en style INLINE, qui l'emporte
 * sur toute classe Tailwind : un `text-[10px]` y serait silencieusement écrasé
 * par le `fontSize: 15px` du variant `body`.
 *
 * Règle qui en découle pour le Lot 6 : un composant définit sa typographie
 * SOIT par `<AppText variant>`, SOIT par des classes — jamais les deux.
 */
export function StatusBadge({
  label,
  tone = "neutral",
  size = "md",
  style,
  className,
}: StatusBadgeProps) {
  const tones = toneClass[tone];
  const sizes = sizeClass[size];

  return (
    <span
      className={[
        "inline-flex self-start rounded-full",
        sizes.wrap,
        tones.wrap,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
    >
      <span className={`font-bold uppercase ${sizes.text} ${tones.text}`}>{label}</span>
    </span>
  );
}
